import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Scale,
  Gavel,
  Stethoscope,
  Clock,
  Users,
  DollarSign,
  AlertCircle,
  RefreshCw,
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const ANDAMENTO_COLORS = {
  em_analise: "#3b82f6",
  exigencia: "#f59e0b",
  concedido: "#10b981",
  justica: "#8b5cf6",
};

const formatDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export function LegalDashboard({
  data,
  period,
  onPeriodChange,
  onRefresh,
  onNavigateToProcesses,
  onNavigateToAgenda,
  getEtapaAtualLabel,
  getAndamentoLabel,
  getTipoProcessoLabel,
  formatCurrency,
}) {
  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500">
        <RefreshCw className="h-8 w-8 animate-spin mb-3" />
        <p>Carregando dashboard...</p>
      </div>
    );
  }

  const andamentoData = Object.entries(data.by_andamento || {}).map(([key, value]) => ({
    name: getAndamentoLabel(key),
    value,
    key,
  }));

  const etapaData = Object.entries(data.by_etapa_atual || {})
    .filter(([key]) => key !== "nenhuma")
    .map(([key, value]) => ({
      name: getEtapaAtualLabel(key),
      value,
    }));

  const tipoData = Object.entries(data.by_tipo || {}).map(([key, value]) => ({
    name: getTipoProcessoLabel(key),
    value,
  }));

  return (
    <div className="space-y-6" data-testid="legal-dashboard">
      {/* Filtros/Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-black">Dashboard Jurídico</h2>
          <p className="text-sm text-gray-500">Visão consolidada de processos, agenda e etapas</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(period)} onValueChange={(v) => onPeriodChange(Number(v))}>
            <SelectTrigger className="w-40 text-black" data-testid="legal-dashboard-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Próximos 7 dias</SelectItem>
              <SelectItem value="15">Próximos 15 dias</SelectItem>
              <SelectItem value="30">Próximos 30 dias</SelectItem>
              <SelectItem value="90">Próximos 90 dias</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={onRefresh} data-testid="legal-dashboard-refresh">
            <RefreshCw className="h-4 w-4 mr-1" />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card
          className="cursor-pointer hover:shadow-lg transition bg-gradient-to-br from-purple-600 to-purple-700 text-white"
          onClick={onNavigateToProcesses}
          data-testid="legal-dash-total-processes"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase opacity-80">Total de Processos</p>
                <p className="text-3xl font-bold mt-1">{data.total_processes}</p>
                <p className="text-xs opacity-80 mt-1">{data.total_clients} cliente(s)</p>
              </div>
              <Scale className="h-10 w-10 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white" data-testid="legal-dash-honorarios">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase opacity-80">Honorários Totais</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(data.total_honorarios)}</p>
                <p className="text-xs opacity-80 mt-1">Contratados</p>
              </div>
              <DollarSign className="h-10 w-10 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-lg transition bg-gradient-to-br from-blue-600 to-blue-700 text-white"
          onClick={onNavigateToAgenda}
          data-testid="legal-dash-audiencias"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase opacity-80">Audiências (próximos {period}d)</p>
                <p className="text-3xl font-bold mt-1">{data.proximas_audiencias?.length || 0}</p>
                <p className="text-xs opacity-80 mt-1">Pendentes</p>
              </div>
              <Gavel className="h-10 w-10 opacity-70" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-lg transition bg-gradient-to-br from-orange-500 to-orange-600 text-white"
          onClick={onNavigateToAgenda}
          data-testid="legal-dash-pericias"
        >
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase opacity-80">Perícias (próximos {period}d)</p>
                <p className="text-3xl font-bold mt-1">{data.proximas_pericias?.length || 0}</p>
                <p className="text-xs opacity-80 mt-1">Pendentes</p>
              </div>
              <Stethoscope className="h-10 w-10 opacity-70" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pizza - Andamento */}
        <Card data-testid="legal-chart-andamento">
          <CardHeader>
            <CardTitle className="text-black text-lg">Processos por Andamento</CardTitle>
          </CardHeader>
          <CardContent>
            {andamentoData.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Sem dados</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={andamentoData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={90}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {andamentoData.map((entry) => (
                      <Cell key={entry.key} fill={ANDAMENTO_COLORS[entry.key] || "#6b7280"} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Barras - Etapa Atual */}
        <Card data-testid="legal-chart-etapa">
          <CardHeader>
            <CardTitle className="text-black text-lg">Distribuição por Etapa Atual</CardTitle>
          </CardHeader>
          <CardContent>
            {etapaData.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhuma etapa definida ainda</p>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={etapaData} margin={{ top: 5, right: 10, left: 0, bottom: 40 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Listas - Próximas Audiências / Perícias / Prazos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Próximas Audiências */}
        <Card data-testid="legal-list-audiencias">
          <CardHeader className="pb-2">
            <CardTitle className="text-black text-base flex items-center gap-2">
              <Gavel className="h-4 w-4 text-blue-600" /> Próximas Audiências
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.proximas_audiencias || data.proximas_audiencias.length === 0) ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhuma audiência nos próximos {period} dias</p>
            ) : (
              <ul className="space-y-2 max-h-60 overflow-y-auto">
                {data.proximas_audiencias.map((ag) => (
                  <li
                    key={ag.id}
                    className="border-l-4 border-blue-500 pl-3 py-2 bg-blue-50 rounded cursor-pointer hover:bg-blue-100 transition"
                    onClick={onNavigateToAgenda}
                  >
                    <p className="text-sm font-bold text-black">{ag.titulo}</p>
                    <p className="text-xs text-gray-600">{formatDateTime(ag.data_hora)}</p>
                    <p className="text-xs text-gray-500">
                      {ag.client_name} · {ag.process_protocolo}
                    </p>
                    {ag.local && <p className="text-xs text-gray-500">📍 {ag.local}</p>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Próximas Perícias */}
        <Card data-testid="legal-list-pericias">
          <CardHeader className="pb-2">
            <CardTitle className="text-black text-base flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-orange-600" /> Próximas Perícias
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.proximas_pericias || data.proximas_pericias.length === 0) ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhuma perícia nos próximos {period} dias</p>
            ) : (
              <ul className="space-y-2 max-h-60 overflow-y-auto">
                {data.proximas_pericias.map((ag) => (
                  <li
                    key={ag.id}
                    className="border-l-4 border-orange-500 pl-3 py-2 bg-orange-50 rounded cursor-pointer hover:bg-orange-100 transition"
                    onClick={onNavigateToAgenda}
                  >
                    <p className="text-sm font-bold text-black">{ag.titulo}</p>
                    <p className="text-xs text-gray-600">{formatDateTime(ag.data_hora)}</p>
                    <p className="text-xs text-gray-500">
                      {ag.client_name} · {ag.process_protocolo}
                    </p>
                    {ag.local && <p className="text-xs text-gray-500">📍 {ag.local}</p>}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Prazos Vencendo */}
        <Card data-testid="legal-list-prazos">
          <CardHeader className="pb-2">
            <CardTitle className="text-black text-base flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-600" /> Prazos Vencendo (30d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!data.prazos_vencendo || data.prazos_vencendo.length === 0) ? (
              <p className="text-sm text-gray-500 text-center py-4">Nenhum prazo crítico</p>
            ) : (
              <ul className="space-y-2 max-h-60 overflow-y-auto">
                {data.prazos_vencendo.map((p) => (
                  <li
                    key={p.id}
                    className="border-l-4 border-red-500 pl-3 py-2 bg-red-50 rounded cursor-pointer hover:bg-red-100 transition"
                    onClick={onNavigateToProcesses}
                  >
                    <p className="text-sm font-bold text-black">Protocolo: {p.numero_protocolo}</p>
                    <p className="text-xs text-gray-600">{p.client_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className="bg-red-600 text-white text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {p.dias_restantes} dia(s)
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {getTipoProcessoLabel(p.tipo_processo)}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Processos por Tipo */}
      {tipoData.length > 0 && (
        <Card data-testid="legal-chart-tipo">
          <CardHeader className="pb-2">
            <CardTitle className="text-black text-lg">Processos por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {tipoData.map((t) => (
                <Badge key={t.name} variant="outline" className="text-sm py-2 px-3 bg-gray-50">
                  {t.name}: <span className="ml-1 font-bold text-purple-700">{t.value}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default LegalDashboard;
